import React, { useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Send, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const productTypes = ["Proteínas", "Pre-entrenos", "Aminoácidos", "Creatina", "Gainers / Carbohidratos", "Vitaminas & Minerales", "Colágeno / Salud", "Otro / a definir"];

export default function Contact() {
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", product_type: "", message: "" });
  const [loading, setLoading] = useState(false);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!form.name || !form.email || !form.message) {
      toast.error("Por favor rellena los campos obligatorios.");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.company) delete payload.company;
      if (!payload.phone) delete payload.phone;
      if (!payload.product_type) delete payload.product_type;
      await api.post("/contact", payload);
      toast.success("¡Solicitud enviada! Te contactaremos en menos de 48h.");
      setForm({ name: "", company: "", email: "", phone: "", product_type: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || "No se pudo enviar el mensaje. Inténtalo de nuevo.";
      toast.error(typeof detail === "string" ? detail : "Error al enviar el mensaje.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contacto" className="relative py-10 md:py-14 bg-gradient-to-br from-[#0A1628] via-[#0B2E4F] to-[#0A1628] text-white overflow-hidden">
      <div className="absolute inset-0 opacity-30 pointer-events-none">
        <div className="hero-blob hero-blob-1" style={{opacity:0.3}} />
        <div className="hero-blob hero-blob-2" style={{opacity:0.3}} />
      </div>
      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-12 items-stretch">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.3em] text-[#5DB2F0] font-semibold mb-4">/ 09 · Contacto</div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-extrabold text-white uppercase tracking-tighter leading-[1]">
              Hablemos de
              <br />
              <span className="text-[#5DB2F0]">tu proyecto.</span>
            </h2>
            <p className="mt-6 text-slate-300 text-lg">Fabricación propia, marca blanca y desarrollo de fórmulas a medida. Cuéntanos tu proyecto y te enviaremos una propuesta técnica sin compromiso.</p>

            <div className="mt-10 space-y-5">
              <InfoLine icon={<Mail className="w-5 h-5" />} label="Email comercial" value="sales@globalnutralabs.com" />
              <InfoLine icon={<Phone className="w-5 h-5" />} label="Teléfono" value="+34 918 834 453" />
              <InfoLine icon={<MapPin className="w-5 h-5" />} label="Planta de producción" value="Calle Salamanca, 25 · 28500 Arganda del Rey, Madrid, España" />
            </div>
          </div>

          <div className="lg:col-span-7 flex">
            <form onSubmit={submit} data-testid="contact-form" className="bg-white rounded-sm p-6 md:p-10 space-y-5 shadow-2xl w-full flex flex-col h-full">
              <div className="grid md:grid-cols-2 gap-5">
                <Field label="Nombre *" htmlFor="c-name">
                  <input id="c-name" data-testid="contact-input-name" required value={form.name} onChange={update("name")} className="input" placeholder="Tu nombre" />
                </Field>
                <Field label="Empresa" htmlFor="c-company">
                  <input id="c-company" data-testid="contact-input-company" value={form.company} onChange={update("company")} className="input" placeholder="Nombre de tu marca" />
                </Field>
              </div>

              <div className="grid md:grid-cols-2 gap-5">
                <Field label="Email *" htmlFor="c-email">
                  <input id="c-email" data-testid="contact-input-email" required type="email" value={form.email} onChange={update("email")} className="input" placeholder="tu@empresa.com" />
                </Field>
                <Field label="Teléfono" htmlFor="c-phone">
                  <input id="c-phone" data-testid="contact-input-phone" value={form.phone} onChange={update("phone")} className="input" placeholder="+34 ..." />
                </Field>
              </div>

              <Field label="Tipo de producto" htmlFor="c-product">
                <select id="c-product" data-testid="contact-select-product" value={form.product_type} onChange={update("product_type")} className="input">
                  <option value="">Selecciona una categoría</option>
                  {productTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>

              <Field label="Cuéntanos tu proyecto *" htmlFor="c-message" grow>
                <textarea id="c-message" data-testid="contact-input-message" required value={form.message} onChange={update("message")} rows={5} className="input resize-none flex-1 min-h-[120px]" placeholder="Producto, volumen estimado, timing, formato, mercado destino..." />
              </Field>

              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-2">
                <p className="text-xs text-slate-500 max-w-md">Al enviar aceptas nuestra política de confidencialidad. No compartiremos tu información con terceros.</p>
                <button type="submit" data-testid="contact-submit-button" disabled={loading} className="btn-brand rounded-sm px-7 py-3.5 text-sm font-semibold inline-flex items-center gap-2 uppercase tracking-wider disabled:opacity-60">
                  {loading ? (<><Loader2 className="w-4 h-4 animate-spin" /> Enviando</>) : (<>Enviar solicitud <Send className="w-4 h-4" /></>)}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <style>{`
        .input {
          width: 100%;
          background: #F8FAFC;
          border: 1px solid #E2E8F0;
          color: #0A1628;
          border-radius: 4px;
          padding: 12px 14px;
          font-size: 14px;
          font-family: 'IBM Plex Sans', sans-serif;
          transition: all .2s ease;
        }
        .input::placeholder { color: #94A3B8; }
        .input:focus { outline: none; border-color: #0A84E2; background: #FFFFFF; box-shadow: 0 0 0 3px rgba(10,132,226,0.15); }
        select.input option { background: #fff; color: #0A1628; }
      `}</style>
    </section>
  );
}

function Field({ label, htmlFor, children }) {
  return (
    <label htmlFor={htmlFor} className="block">
      <span className="block text-xs uppercase tracking-widest text-slate-500 mb-2 font-semibold">{label}</span>
      {children}
    </label>
  );
}

function InfoLine({ icon, label, value }) {
  return (
    <div className="flex items-start gap-4">
      <div className="w-10 h-10 rounded-sm bg-white/10 border border-white/20 text-[#5DB2F0] flex items-center justify-center flex-shrink-0">{icon}</div>
      <div>
        <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold">{label}</div>
        <div className="text-white font-medium">{value}</div>
      </div>
    </div>
  );
}
